import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./identity.css";

const SHIELD = (size = 24) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2l9 3.4v6.4c0 5-3.8 9.3-9 10.2C6.8 21.1 3 16.8 3 11.8V5.4z" fill="#16A34A" />
    <path d="M8.3 12.2l2.6 2.6 4.8-5.2" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const T = {
  it: { aria: "Host verificato, mostra dettagli", close: "Chiudi", title: "Host verificato",
    body: (n) => `${n} è stato identificato con un documento d'identità ufficiale e un selfie tramite la tecnologia di Stripe Identity.`,
    on: "Verificato il", disc: "La verifica riguarda l'identità della persona, non certifica l'alloggio né il suo codice CIN.", report: "Segnala questo profilo" },
  en: { aria: "Verified host, show details", close: "Close", title: "Verified host",
    body: (n) => `${n} has been identified with an official ID document and a selfie through Stripe Identity technology.`,
    on: "Verified on", disc: "Verification concerns the person's identity; it does not certify the accommodation or its CIN code.", report: "Report this profile" },
  fr: { aria: "Hôte vérifié, afficher les détails", close: "Fermer", title: "Hôte vérifié",
    body: (n) => `${n} a été identifié avec une pièce d'identité officielle et un selfie via la technologie Stripe Identity.`,
    on: "Vérifié le", disc: "La vérification concerne l'identité de la personne, elle ne certifie pas le logement ni son code CIN.", report: "Signaler ce profil" },
  de: { aria: "Verifizierter Gastgeber, Details anzeigen", close: "Schließen", title: "Verifizierter Gastgeber",
    body: (n) => `${n} wurde mit einem offiziellen Ausweisdokument und einem Selfie über die Stripe-Identity-Technologie identifiziert.`,
    on: "Verifiziert am", disc: "Die Überprüfung betrifft die Identität der Person; sie zertifiziert weder die Unterkunft noch deren CIN-Code.", report: "Dieses Profil melden" },
  es: { aria: "Anfitrión verificado, mostrar detalles", close: "Cerrar", title: "Anfitrión verificado",
    body: (n) => `${n} ha sido identificado con un documento de identidad oficial y un selfie mediante la tecnología de Stripe Identity.`,
    on: "Verificado el", disc: "La verificación se refiere a la identidad de la persona; no certifica el alojamiento ni su código CIN.", report: "Denunciar este perfil" },
};

function fmtDate(v, lang) {
  if (!v) return "";
  try {
    const d = new Date(v.length <= 10 ? v + "T00:00:00" : v);
    if (isNaN(d)) return v;
    const loc = { it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES" }[lang] || "it-IT";
    return d.toLocaleDateString(loc, { day: "2-digit", month: "long", year: "numeric" });
  } catch { return v; }
}

export function HostVerifiedShield({ name, verifiedAt, lang = "it", size = 22, onReport, variant }) {
  const t = T[lang] || T.it;
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const closeRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      closeRef.current?.focus();
      const onKey = (e) => { if (e.key === "Escape") close(); };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    } else if (wasOpen.current) {
      wasOpen.current = false;
      btnRef.current?.focus();
    }
  }, [open, close]);

  const dateText = fmtDate(verifiedAt, lang);

  const isBadge = variant === "badge";
  const isPill = variant === "pill";
  const badgeStyle = {
    background: "transparent", border: 0, padding: 0, cursor: "pointer",
    display: "flex", lineHeight: 0, outline: "none",
    WebkitTapHighlightColor: "transparent",
    filter: "drop-shadow(0 2px 3px rgba(0,0,0,.45))",
  };
  const pillStyle = {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "#16A34A", color: "#fff", border: 0,
    borderRadius: 999, padding: "6px 13px 6px 10px", cursor: "pointer",
    fontSize: 13, fontWeight: 700, lineHeight: 1, outline: "none",
    WebkitTapHighlightColor: "transparent",
    boxShadow: "0 6px 16px -8px rgba(22,163,74,.9)",
    fontFamily: "inherit", whiteSpace: "nowrap",
  };

  const shieldSvg = (st, sz = 15) => (
    <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={st} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </svg>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={isBadge || isPill ? undefined : "hlv-shield-btn"}
        style={isBadge ? badgeStyle : isPill ? pillStyle : undefined}
        aria-label={t.aria}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        data-testid="host-verified-shield"
      >
        {isBadge ? SHIELD(30) : isPill ? (<>{shieldSvg("#fff")}<span>{t.title}</span></>) : SHIELD(size)}
      </button>

      {createPortal(
        <div className={`hlv-modal${open ? " open" : ""}`} role="dialog" aria-modal="true"
          aria-label={t.title}
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          data-testid="host-verified-modal">
          <div className="hlv-dialog">
            <button ref={closeRef} type="button" className="hlv-close" aria-label={t.close}
              onClick={close} data-testid="host-verified-close">×</button>
            <div className="t">{SHIELD(28)}{t.title}</div>
            <p>{t.body(name || "")}</p>
            {dateText && <small>{t.on} {dateText}</small>}
            <div className="disc">{t.disc}</div>
            <div className="row">
              <button type="button" className="report"
                onClick={() => { close(); onReport && onReport(); }}
                data-testid="host-report-profile">{t.report}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default HostVerifiedShield;
